/**
 * Coordinates "session expired" handling between the API client and the UI.
 * When a request gets 401 after the app has started, the client pauses it here;
 * the re-authentication dialog resolves the gate, and paused requests retry.
 */
type Listener = () => void;

let pending: Promise<boolean> | null = null;
let resolvePending: ((ok: boolean) => void) | null = null;
const listeners = new Set<Listener>();
let enabled = false;

export const authGate = {
  /** Enabled once a session has been established; before that, 401 means "go to login". */
  enable() {
    enabled = true;
  },
  disable() {
    enabled = false;
    authGate.resolve(false);
  },
  get isEnabled() {
    return enabled;
  },
  /** Called by the client on 401. Resolves true after successful re-auth. */
  waitForReauth(): Promise<boolean> {
    if (!enabled) return Promise.resolve(false);
    if (!pending) {
      pending = new Promise<boolean>((resolve) => {
        resolvePending = resolve;
      });
      listeners.forEach((l) => l());
    }
    return pending;
  },
  get isWaiting() {
    return pending !== null;
  },
  resolve(ok: boolean) {
    resolvePending?.(ok);
    pending = null;
    resolvePending = null;
    listeners.forEach((l) => l());
  },
  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
